/* eslint-disable max-classes-per-file */
import { isTouchDevice } from './helpers';

/**
 * An object that contains a min and max screensize,
 * as well as methods to check if the window is in range
 * of those screensizes.
 *
 * @since 1.0.0
 *
 * @type {Class}
 */
export class Breakpoint {
	constructor(value) {
		this.value = value;
	}

	above() {
		const mq = window.matchMedia(`(min-width: ${this.value}px)`);
		return mq.matches;
	}

	below() {
		const mq = window.matchMedia(`(max-width: ${this.value - 1}px)`);
		return mq.matches;
	}

	aboveOrTouch() {
		return this.above() || isTouchDevice();
	}

	belowOrTouch() {
		return this.below() || isTouchDevice();
	}
}

/**
 * A collection of Breakpoint objects for easy access in your project.
 * Best if these match the breakpoint values you setup in your CSS.
 *
 * @since 1.0.0
 *
 * @type {Class}
 */
export class Breakpoints {
	constructor(sizes) {
		this.mobileMenuSize = 960;

		const defaults = [
			{
				key: 'xs',
				value: 480,
			},
			{
				key: 'sm',
				value: 640,
			},
			{
				key: 'md',
				value: 850,
			},
			{
				key: 'mobileMenu',
				value: this.mobileMenuSize,
			},
			{
				key: 'lg',
				value: 1120,
			},
			{
				key: 'xl',
				value: 1380,
			},
		];

		this.sizes = sizes || defaults;
		this.hasMouse = matchMedia('(pointer:fine)').matches;

		for (let i = 0; i < this.sizes.length; i++) {
			const { key, value } = this.sizes[i];
			this[key] = new Breakpoint(value);
		}
	}

	/**
	 * Check if the window width is above the passed breakpoint or pixel value.
	 *
	 * Example...
	 *
	 * import { breakpoints } from '../variables';
	 * if (breakpoints.above('sm')) {...}
	 * if (breakpoints.above(600)) {...}
	 *
	 * @param {string|integer} value The min breakpoint size. Can be either a key matching an existing breakpoint, or an integer.
	 */
	above(value) {
		const bp = this[value] || new Breakpoint(value);
		if (bp) {
			return bp.above();
		}

		return false;
	}

	/**
	 * Check if the window width is below the passed breakpoint or pixel value.
	 *
	 * Example...
	 *
	 * import { breakpoints } from '../variables';
	 * if (breakpoints.below('sm')) {...}
	 * if (breakpoints.below(600)) {...}
	 *
	 * @param {string|integer} value The min breakpoint size. Can be either a key matching an existing breakpoint, or an integer.
	 */
	below(value) {
		const bp = this[value] || new Breakpoint(value);
		if (bp) {
			return bp.below();
		}

		return false;
	}

	/**
	 * Check if the window width is in between two breakpoints or pixel values.
	 *
	 * Example...
	 *
	 * import { breakpoints } from '../variables';
	 * if (breakpoints.between('sm, 'lg')) {...}
	 * if (breakpoints.between(600, 900)) {...}
	 *
	 * @param {string|integer} min The min breakpoint size. Can be either a key matching an existing breakpoint, or an integer.
	 * @param {string|integer} max The max breakpoint size. Can be either a key matching an existing breakpoint, or an integer.
	 */
	between(min, max) {
		const minBp = this[min] || new Breakpoint(min);
		const maxBp = this[max] || new Breakpoint(max);

		if (minBp && maxBp) {
			return minBp.above() && maxBp.below();
		}

		return false;
	}

	isMobileMenu() {
		const { hasMouse } = this;
		return window.innerWidth < this.mobileMenuSize || ('ontouchstart' in window && !hasMouse) || (navigator.maxTouchPoints > 0 && !hasMouse) || (navigator.msMaxTouchPoints > 0 && !hasMouse);
	}
}
